import React, { useEffect, useRef, useState } from "react";
import { Env, getAdminApiUrl } from "../../shared";
import { useBusinesses } from "../../businesses/api/useBusinesses";
import { FieldLabel, inputClass } from "./FieldLabel";
import { BusinessHours } from "./closingTime";

export interface PickedBusiness {
  id: string;
  name: string;
  timezone: string | null;
  /** Weekly hours, carried so the deal form can resolve a closing time without
   *  a second round trip. Shape varies by endpoint — see normalizeHours. */
  hours?: BusinessHours | null;
}

const SEARCH_DEBOUNCE_MS = 400;
const RESULT_LIMIT = 10;

/** Debounced business search shared by the single- and multi-select pickers. */
function useBusinessSearch(env: Env, query: string) {
  const [debounced, setDebounced] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebounced(query), SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  return useBusinesses({
    search: debounced,
    apiUrl: getAdminApiUrl(env),
    fields: "id,name,timezone,hours",
    limit: RESULT_LIMIT,
  });
}

/** Closes the dropdown on an outside click or Escape. */
function useDismissOnOutside(open: boolean, close: () => void) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  return containerRef;
}

function DropdownStatus({
  loading, error, empty,
}: {
  loading: boolean;
  error: string | null;
  empty: boolean;
}) {
  if (loading) return <p className="px-3.5 py-2.5 text-sm text-gray-500">Searching…</p>;
  if (error) return <p className="px-3.5 py-2.5 text-sm text-red-400">Couldn't load businesses. Try again.</p>;
  if (empty) return <p className="px-3.5 py-2.5 text-sm text-gray-500">No businesses match.</p>;
  return null;
}

export function BusinessPicker({
  env, value, onChange,
}: {
  env: Env;
  value: PickedBusiness | null;
  onChange: (b: PickedBusiness | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const { businesses, loading, error } = useBusinessSearch(env, query);
  const containerRef = useDismissOnOutside(open, () => setOpen(false));

  // When a business is selected, show its name in the field and hide the list.
  if (value) {
    return (
      <div>
        <FieldLabel required>Business</FieldLabel>
        <div className="flex items-center gap-2">
          <input readOnly value={value.name} className={inputClass} />
          <button
            type="button"
            onClick={() => { onChange(null); setQuery(""); setOpen(true); }}
            className="px-3 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-xl hover:text-white hover:border-violet-500 transition-all"
          >
            Change
          </button>
        </div>
        {value.timezone
          ? <p className="text-xs text-gray-500 mt-1">Times shown in <span className="text-violet-300">{value.timezone}</span>.</p>
          : <p className="text-xs text-amber-400 mt-1">⚠ This business has no timezone set.</p>}
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <FieldLabel required>Business</FieldLabel>
      <input
        type="text"
        value={query}
        placeholder="Search businesses…"
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className={inputClass}
      />
      {open && query.trim() !== "" && (
        <div className="absolute z-20 mt-1 w-full bg-gray-900 border border-gray-700 rounded-xl shadow-xl max-h-64 overflow-y-auto">
          <DropdownStatus loading={loading} error={error} empty={!loading && !error && businesses.length === 0} />
          {!loading && !error && businesses.map(b => (
            <button
              key={b.id}
              type="button"
              onClick={() => { onChange({ id: b.id, name: b.name, timezone: b.timezone, hours: (b as any).hours ?? null }); setOpen(false); }}
              className="w-full text-left px-3.5 py-2.5 text-sm text-gray-200 hover:bg-gray-800 transition-colors"
            >
              {b.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Multi-select sibling of `BusinessPicker`, for a campaign's eligible-business
 * set. Same debounced search and dropdown; picks accumulate as removable chips
 * and the dropdown stays open so several venues can be added in one pass.
 *
 * An empty selection is meaningful, not missing: a campaign with no eligible
 * businesses is redeemable anywhere, so the field says that instead of showing a
 * blank.
 */
export function BusinessMultiPicker({
  env, value, onChange, label = "Eligible businesses", emptyHint,
}: {
  env: Env;
  value: PickedBusiness[];
  onChange: (b: PickedBusiness[]) => void;
  label?: string;
  emptyHint?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const { businesses, loading, error } = useBusinessSearch(env, query);
  const containerRef = useDismissOnOutside(open, () => setOpen(false));

  const selectedIds = new Set(value.map(b => b.id));
  const toggle = (b: PickedBusiness) => {
    onChange(selectedIds.has(b.id) ? value.filter(v => v.id !== b.id) : [...value, b]);
  };

  return (
    <div className="relative" ref={containerRef}>
      <FieldLabel>{label}</FieldLabel>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map(b => (
            <span
              key={b.id}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-violet-950/40 border border-violet-800/60 rounded-lg text-xs text-violet-200"
            >
              <span className="truncate max-w-[220px]">{b.name}</span>
              <button
                type="button"
                onClick={() => onChange(value.filter(v => v.id !== b.id))}
                className="text-violet-400 hover:text-white"
                aria-label={`Remove ${b.name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        type="text"
        value={query}
        placeholder="Search businesses to add…"
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className={inputClass}
      />
      <p className="text-xs text-gray-500 mt-1">
        {value.length === 0
          ? (emptyHint ?? "No venues selected — the deal is not limited to any business.")
          : `${value.length} ${value.length === 1 ? "business" : "businesses"} selected.`}
      </p>

      {open && query.trim() !== "" && (
        <div className="absolute z-20 mt-1 w-full bg-gray-900 border border-gray-700 rounded-xl shadow-xl max-h-64 overflow-y-auto">
          <DropdownStatus loading={loading} error={error} empty={!loading && !error && businesses.length === 0} />
          {!loading && !error && businesses.map(b => {
            const picked = selectedIds.has(b.id);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => toggle({ id: b.id, name: b.name, timezone: b.timezone })}
                className="w-full flex items-center justify-between gap-2 text-left px-3.5 py-2.5 text-sm text-gray-200 hover:bg-gray-800 transition-colors"
              >
                <span className="truncate">{b.name}</span>
                <span className={picked ? "text-violet-300 shrink-0" : "text-gray-600 shrink-0"}>
                  {picked ? "✓ added" : "+ add"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
